import type { Router } from 'vue-router'
import { isChunkLoadErrorMessage } from '@/utils/chunkLoadError'

const CHUNK_RELOAD_KEY = 'chunkReloadAt'
const CHUNK_RELOAD_COOLDOWN_MS = 10_000

/**
 * Reload into the new build when a lazy page's chunk fails to load.
 *
 * Every deploy deletes the previous build's chunks, and CloudFront answers a missing
 * chunk with index.html. A tab still running the old build then fails every lazy route
 * and its nav links do nothing. Vite flags each such failure with `vite:preloadError`,
 * whatever the browser's message says (Safari's wording slipped past a message match
 * in FLX-4002), so that event is the primary signal and the messages are the fallback.
 */
export const installChunkReload = (router: Router) => {
  const chunkLoadErrors = new WeakSet<object>()
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    if (payload && typeof payload === 'object') chunkLoadErrors.add(payload)
  })

  router.onError((error, to) => {
    if (!chunkLoadErrors.has(error) && !isChunkLoadErrorMessage(error?.message)) return

    // Reload once to pick up the new build. If the chunk is still missing right after
    // a reload, stop instead of looping; the error stays in the console and Sentry.
    try {
      const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY))
      if (lastReload && Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    } catch {
      // sessionStorage can be unavailable (private mode); reload anyway
    }
    window.location.assign(to.fullPath)
  })
}
